import type {Tag} from 'folder-items/FileConstants';
import {findSchemaTagByApiId} from 'schemas/tags/TagUtil';

import {
  aaSequenceId,
  getPredictionInputComponentsField,
  moleculeId,
  type PredictionConfig,
  type PredictionInputComponent,
} from '../input-forms/BulkPredictionUtils';
import type {ScientificModelNode} from '../ScientificModelMetadata';
import {MODEL_INPUT_FIELD_VALUE_TYPE} from './CustomEntityInputTypeDefinitions';
import type {CustomEntityFieldMapping, CustomEntityInputFieldMapping} from './CustomEntitySelectionSetup';

export interface CustomEntityWithTags {
  id: string;
  name: string;
  tags: Array<Tag>;
}

function getMappedInputComponent(
  entity: CustomEntityWithTags,
  mapping: CustomEntityInputFieldMapping
): PredictionInputComponent {
  const fieldValue = findSchemaTagByApiId(entity.tags, mapping.schemaFieldApiId);
  if (fieldValue?.targetFiles?.length !== 1) {
    throw new Error(
      `${entity.name} must have exactly one value in ${mapping.schemaFieldName} for ${mapping.inputFieldDisplayName}.`
    );
  }
  const targetId = fieldValue.targetFiles[0].id;
  if (mapping.valueType === MODEL_INPUT_FIELD_VALUE_TYPE.AMINO_ACID_SEQUENCE) {
    return aaSequenceId(targetId);
  }
  if (mapping.valueType === MODEL_INPUT_FIELD_VALUE_TYPE.MOLECULE) {
    return moleculeId(targetId);
  }
  throw new Error(`Unsupported model input field value type: ${mapping.valueType}`);
}

export function buildCustomEntityPredictionConfigs(
  entities: Array<CustomEntityWithTags>,
  mapping: CustomEntityFieldMapping,
  model: ScientificModelNode
): Array<PredictionConfig> {
  return entities.map((entity) => {
    const mappedFields = mapping.fieldMappings.map((fieldMapping) => ({
      component: getMappedInputComponent(entity, fieldMapping),
      key: fieldMapping.inputFieldKey,
    }));
    if (mappedFields.length === 1) {
      const configField = getPredictionInputComponentsField(model);
      return {
        [configField]: configField === 'component' ? mappedFields[0].component : [mappedFields[0].component],
      };
    }
    return {
      [mapping.inputType]: Object.fromEntries(mappedFields.map(({component, key}) => [key, component])),
    };
  });
}
